/**
 * staticstructure.ts — draw the full-course STATIC structure. Compute nothing.
 *
 * Card `t_fb5e844d`. Every number arrives from `/api/overlays/{a}/{tf}/static_structure?n=`, which
 * is `ict.static_structure` (parent `t_509335f4`; the rules and their VTT timecodes live in that
 * module's docstring). This file maps rows to shapes and decides no rule — `overlays.ts`' standing
 * contract: geometry in, pixels out.
 *
 *   points     a marker on the extreme bar labelled HH / HL / LL / LH. A `seed` point (the very
 *              first structure, or the extreme a break installs) reads `HL seed` etc. — the author
 *              never says where the first structure comes from, so it is never shown as confirmed.
 *   levels     a 1px band at each point's price FROM its `knowable_time` (the close it became
 *              determined on) TO `end_time` (the knowable bar of the HL / LH that replaced it), or to
 *              the right edge while it stands. Nothing is drawn before the bar it was known on.
 *   breaks     a marker on the closing bar that broke the protective HL / LH. Its level line is
 *              already the protective point's band, which ends on that bar.
 *   order flow a zone from the previous HH to the HL it confirmed (LL to LH), [00:31:03], from its
 *              knowable bar to its HL / LH's end.
 *   fractals   a small unlabelled dot per VALID fractal (internal liquidity), on its own bar.
 *
 * `n` (the fractal bar count): the route requires it; the knob starts on `STATIC_N_DEFAULT` = 1,
 * the THREE-candle fractal (card `t_28a039b9`, bp_method.md §5 row 1). The course never says the
 * count in words (§2.2), but the channel lesson its chapter is cut from shows it on a slide:
 * «Внутренняя Ликвидность / Трехсвечной фрактал» [F 015 95], voiced at 015 01:23-01:39 as the
 * structure's internal liquidity. The 2023 frame that seemed to conflict [F 021 514] (WICK.ED
 * Fractals, «Periods 2») sets its bar-count field «3 or 5 Bar Fractal» to 3 as well; what «Periods»
 * does in that script is not on screen (`process/reviews/t_28a039b9/shots/F021_514.png`). The other
 * choices stay for comparison. NOT A SIGNAL: no entry, no stop, no size.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';

/** Bars each side of a fractal the knob offers, and the one it starts on: 1 = 3-candle [F 015 95]. */
export const STATIC_N_CHOICES = ['1', '2', '3', '5'] as const;
export const STATIC_N_DEFAULT: (typeof STATIC_N_CHOICES)[number] = '1';

export type StructureKind = 'HH' | 'HL' | 'LL' | 'LH';

export type StructurePoint = {
  kind: StructureKind;
  idx: number;
  time: UTCTimestamp;
  price: number;
  knowable_idx: number;
  knowable_time: UTCTimestamp;
  seed: boolean;
  /** The knowable bar of the HL / LH that replaced it; null while it stands. */
  end_idx: number | null;
  end_time: UTCTimestamp | null;
};

export type StructureBreak = {
  direction: 'up' | 'down';
  level_kind: StructureKind;
  level: number;
  level_time: UTCTimestamp;
  idx: number;
  time: UTCTimestamp;
};

export type StructureFlow = {
  direction: 'long' | 'short';
  from_kind: StructureKind;
  from_price: number;
  from_time: UTCTimestamp;
  to_kind: StructureKind;
  to_price: number;
  to_time: UTCTimestamp;
  /** The zone's own bar: its HL / LH's confirming close. */
  time: UTCTimestamp;
  knowable_time: UTCTimestamp;
  end_time: UTCTimestamp | null;
};

export type StructureFractal = {
  kind: 'high' | 'low';
  idx: number;
  time: UTCTimestamp;
  price: number;
};

export type StaticStructPayload = {
  component: 'static_structure';
  source: string;
  params: { n: number; n_note: string };
  counts: Record<string, number>;
  points: StructurePoint[];
  breaks: StructureBreak[];
  order_flow: StructureFlow[];
  fractals: StructureFractal[];
};

const UP = 'rgba(38, 166, 154, 0.95)';
const DOWN = 'rgba(239, 83, 80, 0.95)';
/** A seed is the author's UNSPECIFIED start, so it is drawn in a muted colour of its own. */
const SEED = 'rgba(158, 158, 158, 0.95)';
const FRACTAL = 'rgba(120, 144, 156, 0.8)';

const isHigh = (k: StructureKind) => k === 'HH' || k === 'LH';
const colorOf = (p: StructurePoint) => (p.seed ? SEED : isHigh(p.kind) ? DOWN : UP);

/** The marker / band caption: `HL` or `HL seed`. One function, so the two cannot disagree. */
export const pointLabel = (p: StructurePoint): string => (p.seed ? `${p.kind} seed` : p.kind);

/** A band anchored before the pane's first bar starts at that bar instead (`RangePrimitive` draws
 *  NOTHING for a band anchored before the series; `sessions.ts` clamps for the same reason). The
 *  server keeps such a row precisely because part of it is on screen (`main._trim_by_time`). */
const clamp = (t: UTCTimestamp, first: number | null): UTCTimestamp =>
  (first != null && t < first ? first : t) as UTCTimestamp;

export function levelBands(
  p: StaticStructPayload,
  windowFirstTime: number | null = null,
): ZoneBand[] {
  return p.points.map((q) => ({
    lo: q.price,
    hi: q.price,
    fromTime: clamp(q.knowable_time, windowFirstTime),
    toTime: q.end_time,
    fill: colorOf(q),
    border: colorOf(q),
    label: `${pointLabel(q)} ${q.price}`,
    exactSpan: true,
  }));
}

export function flowBands(
  p: StaticStructPayload,
  windowFirstTime: number | null = null,
): ZoneBand[] {
  return p.order_flow.map((f) => {
    const long = f.direction === 'long';
    return {
      lo: Math.min(f.from_price, f.to_price),
      hi: Math.max(f.from_price, f.to_price),
      fromTime: clamp(f.knowable_time, windowFirstTime),
      toTime: f.end_time,
      fill: long ? 'rgba(38, 166, 154, 0.08)' : 'rgba(239, 83, 80, 0.08)',
      border: long ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
      label: `order flow ${f.from_kind}→${f.to_kind}`,
      exactSpan: true,
    };
  });
}

/** A marker whose bar is left of the pane is NOT drawn: moving it to the first bar would date the
 *  point wrongly. Its level band still reaches into the pane. */
export function structureMarkers(
  p: StaticStructPayload,
  windowFirstTime: number | null = null,
): SeriesMarker<Time>[] {
  const inPane = (t: number) => windowFirstTime == null || t >= windowFirstTime;
  const out: SeriesMarker<Time>[] = [];
  for (const q of p.points) {
    if (!inPane(q.time)) continue;
    const high = isHigh(q.kind);
    out.push({
      time: q.time,
      position: high ? 'aboveBar' : 'belowBar',
      color: colorOf(q),
      shape: high ? 'arrowDown' : 'arrowUp',
      text: pointLabel(q),
    });
  }
  for (const b of p.breaks) {
    if (!inPane(b.time)) continue;
    const down = b.direction === 'down';
    out.push({
      time: b.time,
      position: down ? 'belowBar' : 'aboveBar',
      color: down ? DOWN : UP,
      shape: 'square',
      text: down ? `break ↓ ${b.level_kind}` : `break ↑ ${b.level_kind}`,
    });
  }
  for (const f of p.fractals) {
    if (!inPane(f.time)) continue;
    out.push({
      time: f.time,
      position: f.kind === 'high' ? 'aboveBar' : 'belowBar',
      color: FRACTAL,
      shape: 'circle',
      size: 0.4,
      text: '',
    });
  }
  return out.sort((a, b) => (a.time as number) - (b.time as number));
}

export function staticStructureSummary(p: StaticStructPayload): [string, string][] {
  const c = (k: string) => String(p.counts[k] ?? 0);
  return [
    ['static structure', `n=${p.params.n} — fractal bar count UNSPECIFIED BY AUTHOR, your choice`],
    ['points', `${c('total')} (HH ${c('HH')} / HL ${c('HL')} / LL ${c('LL')} / LH ${c('LH')}), seeds marked`],
    ['breaks (body close)', String(p.breaks.length)],
    ['order-flow zones', String(p.order_flow.length)],
    ['valid fractals', String(p.fractals.length)],
    ['what this is', 'full-course structure, closed bars only — NOT an entry signal'],
  ];
}

/** ONE CALL for every shape and the rows describing them (`sessionsDrawing`'s shape). */
export function staticStructureDrawing(
  p: StaticStructPayload,
  windowFirstTime: number | null = null,
): { bands: ZoneBand[]; markers: SeriesMarker<Time>[]; rows: [string, string][] } {
  return {
    // Zones first so the level lines paint over them.
    bands: [...flowBands(p, windowFirstTime), ...levelBands(p, windowFirstTime)],
    markers: structureMarkers(p, windowFirstTime),
    rows: staticStructureSummary(p),
  };
}
